import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import Heading from 'LuxKit/Typography/Heading'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { type BasicTrip, type FullTrip } from 'tripPlanner/types/common'
import { calculateDays, formatCasualDateRange, getItemConfirmationCode, getTripURL } from 'tripPlanner/utils'
import moment from 'moment'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import TripSelect from './TripSelect/TripSelect'
import ModalContext from 'contexts/ModalContext'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import TripCarousel from './TripCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import GradientAnimatedBackground from 'tripPlanner/Tripkit/GradientAnimatedBackground'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useOrders from 'hooks/Order/useOrders'
import type { TripData } from 'home/hooks/useHomePageTripData'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import CategoryDropdown from './CategoryDropdown'
import OrderDropdown from './OrderDropdown'
import { useOrderDropdown } from 'home/hooks/useDropdownLogic'
import type { ExploreCategory } from 'tripPlanner/pages/trip/Explore/common'
import { exploreCategories } from 'tripPlanner/pages/trip/Explore/common'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'

const BackgroundContainer = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
`

const StyledGradientAnimatedBackground = styled(GradientAnimatedBackground)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;

  & > div {
    ${mediaQueryUp.tablet} {
      background-position: center;
    }
  }
`

const StyledGroup = styled(Group)`
display: none;
  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  pointer-events: auto;
`

interface Props {
  basicTrip?: BasicTrip
  trip?: FullTrip
  onSelectTrip: (tripId: string) => void
  tripData: TripData
  trips: Array<BasicTrip>
  onDismiss: () => void
}

function TripHomePage({ basicTrip, trip, onSelectTrip, tripData, trips, onDismiss }: Props) {
  const intl = useIntl()
  const [usersOrders] = useOrders('upcoming')

  const upcomingTrips = useMemo(() => {
    const now = moment()
    return trips.filter((trip) => {
      return !trip.endDate || trip.endDate.clone().add(1, 'd').isSameOrAfter(now, 'day')
    })
  }, [trips])

  const ordersWithCoordinates = useMemo(() => {
    if (!usersOrders || usersOrders.length === 0) return []

    return usersOrders.filter(order => {
      if (order.items.length > 0) {
        return order.items[0].offer?.property?.latitude && order.items[0].offer?.property?.longitude
      } else if (order.bedbankItems.length > 0) {
        return order.bedbankItems[0].offer?.property?.latitude && order.bedbankItems[0].offer?.property?.longitude
      }
      return false
    })
  }, [usersOrders])

  const daysToGo = useMemo(() => {
    if (!basicTrip?.startDate) return 0
    return calculateDays(moment(), basicTrip.startDate) || 0
  }, [basicTrip?.startDate])

  const showModal = useContext(ModalContext)

  const [selectedCategory, setSelectedCategory] = useState<ExploreCategory>(exploreCategories[0])

  const {
    selectedOrderTripData,
    isOrderDropdownOpen,
    orderDropdownRef,
    orderButtonRef,
    orderDestinationItems,
    handleOrderDropdownToggle,
    handleOrderDropdownClose,
    handleOrderClick,
  } = useOrderDropdown(ordersWithCoordinates)

  const handleCategoryChange = useCallback((category: ExploreCategory) => {
    setSelectedCategory(category)
  }, [])

  const handleSelectTrip = useCallback(() => {
    showModal(
      <TripSelect onSelectTrip={onSelectTrip} />,
    )
  }, [onSelectTrip, showModal])

  const currentTripData = useMemo(() => {
    if (selectedOrderTripData?.lat && selectedOrderTripData?.lng) {
      return {
        lat: selectedOrderTripData.lat,
        lng: selectedOrderTripData.lng,
        location: selectedOrderTripData.location,
        showDistance: true,
      }
    }

    let lat = basicTrip?.destinationsGeo?.[0]?.point?.lat ?? tripData.lat
    let lng = basicTrip?.destinationsGeo?.[0]?.point?.lng ?? tripData.lng
    let showDistance = false

    const hotelItem = trip?.items.find(item =>
      item.type === 'ACCOMMODATION' &&
      basicTrip?.orderIds?.some(orderId => getItemConfirmationCode(item) === orderId),
    )
    if (hotelItem?.type === 'ACCOMMODATION' && 'point' in hotelItem.place && hotelItem.place.point.lat && hotelItem.place.point.lng) {
      lat = hotelItem.place.point.lat
      lng = hotelItem.place.point.lng
      showDistance = true
    }

    return {
      lat,
      lng,
      location: basicTrip?.destinationsGeo?.[0]?.shortName || tripData.location,
      showDistance,
    }
  }, [selectedOrderTripData, basicTrip, trip, tripData])

  if (!basicTrip?.destinationsGeo?.length && !tripData.location && !currentTripData.location) {
    return null
  }

  const daysToGoLabel = intl.formatMessage({
    id: 'INJol6lKsu',
    defaultMessage: '{count, plural, one {# Day} other {# Days}} to go',
    description: 'Countdown showing days until trip in the trip planner feature',
  }, { count: daysToGo })
  const continuePlanningLabel = intl.formatMessage({
    id: 'WH9noJ5ZeP',
    defaultMessage: 'Continue planning',
    description: 'Heading text when user has an existing trip to continue planning',
  })
  const discoverBestOfLabel = intl.formatMessage({
    id: 'Kkd/4Yj3HP',
    defaultMessage: 'Discover the best of',
    description: 'Heading text when discovering a new destination on the homepage',
  })
  const startPlanningLabel = intl.formatMessage({
    id: 'fVdHuhq+YO',
    defaultMessage: 'Start planning - It’s free',
    description: 'Button label to start planning a trip in the trip planner feature',
  })

  return (
    <BackgroundContainer>
      <StyledGradientAnimatedBackground />
      <ContentWrapper>
        <Sheet paletteType="inverse" padding="40 0">
          <Group direction="vertical" gap={20} desktopGap={24}>

            <LayoutContainer>
              <Group direction="vertical" gap={20} desktopGap={24}>
                <Group direction="vertical" gap={4}>
                  <CSSBreakpoint max="mobile">
                    {daysToGo > 0 && basicTrip?.startDate && basicTrip?.endDate && <PromotionLabel variant="default">
                      {daysToGoLabel}
                    </PromotionLabel>}
                  </CSSBreakpoint>
                  <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                    <Group direction="horizontal" gap={8} verticalAlign="center">
                      <Heading variant="heading2">
                        {basicTrip ? continuePlanningLabel : discoverBestOfLabel} <i>{basicTrip?.name || currentTripData.location}</i> {upcomingTrips.length > 1 && <IconButton
                          kind="tertiary"
                          size="small"
                          children={<LineAngleDownIcon />}
                          onClick={handleSelectTrip}
                        />}
                      </Heading>
                      {!basicTrip && <OrderDropdown
                        orders={ordersWithCoordinates}
                        isOrderDropdownOpen={isOrderDropdownOpen}
                        orderDropdownRef={orderDropdownRef}
                        orderButtonRef={orderButtonRef}
                        orderDestinationItems={orderDestinationItems}
                        handleOrderDropdownToggle={handleOrderDropdownToggle}
                        handleOrderDropdownClose={handleOrderDropdownClose}
                        handleOrderClick={handleOrderClick}
                      />}
                    </Group>
                    <IconButton
                      kind="tertiary"
                      size="small"
                      children={<LineTimesIcon />}
                      onClick={onDismiss}
                    />
                  </Group>
                  {basicTrip && <Group direction="horizontal" gap={8} verticalAlign="center" horizontalAlign="start">
                    <CSSBreakpoint min="tablet">
                      {basicTrip.startDate && basicTrip.endDate && daysToGo > 0 && <PromotionLabel variant="default">
                        {daysToGoLabel}
                      </PromotionLabel>}
                    </CSSBreakpoint>
                    {basicTrip.destinationsGeo?.[0] &&
                      <><LineMapMarkerIcon /><BodyText variant="medium">
                        {basicTrip.destinationsGeo[0].shortName}
                      </BodyText>
                      </>}
                    {basicTrip.destinationsGeo && basicTrip.startDate && basicTrip.endDate &&
                      <BodyText variant="medium">
                        {intl.formatMessage(middleDotSeparatorMessage)}
                      </BodyText>}
                    {basicTrip.startDate && basicTrip.endDate &&
                      <>
                        <LineCalendarIcon />
                        <BodyText variant="medium">
                          {formatCasualDateRange(basicTrip.startDate, basicTrip.endDate)}
                        </BodyText>
                      </>}
                    <StyledGroup direction="horizontal" gap={8} verticalAlign="center">
                      {trip?.travellerRooms && trip.travellerRooms.length > 0
                        && <BodyText variant="medium">
                          {intl.formatMessage(middleDotSeparatorMessage)}
                        </BodyText>}
                      {trip?.travellerRooms && trip.travellerRooms.length > 0
                        && <>
                          <LineUsersAltIcon />
                          <BodyText variant="medium">
                            {formatOccupantsShort(trip.travellerRooms)}
                          </BodyText>
                        </>}
                    </StyledGroup>
                  </Group>}
                  {!basicTrip &&
                    <BodyText variant="medium">
                      {intl.formatMessage({
                        id: 'O8h/LBRqiP',
                        defaultMessage: 'Discover more with AI - must-see landmarks and hidden gems, planned for your pace and preferences.',
                        description: 'Description of AI-powered trip discovery features on the homepage',
                      })}
                    </BodyText>}
                </Group>
              </Group>
            </LayoutContainer>
            <Group direction="vertical" gap={12}>
              <LayoutContainer>
                <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
                  <Heading variant="heading6">
                    {intl.formatMessage({
                      id: '5hA6WSGHC8',
                      defaultMessage: 'Attractions to hidden gems',
                      description: 'Section heading for the explore carousel on the homepage',
                    })}
                  </Heading>
                  <Group direction="horizontal" gap={12} verticalAlign="center">
                    <CSSBreakpoint min="tablet">
                      <BodyText variant="small">
                        {intl.formatMessage({
                          id: 'nZ0evt/RRN',
                          defaultMessage: 'Show:',
                          description: 'Label before category filter dropdown in the search or listing filters panel',
                        })}
                      </BodyText>
                    </CSSBreakpoint>
                    <CategoryDropdown onCategoryChange={handleCategoryChange} />
                  </Group>
                </Group>
              </LayoutContainer>
              <TripCarousel
                type={selectedCategory.label}
                lat={currentTripData.lat}
                lng={currentTripData.lng}
                location={currentTripData.location}
                tripId={basicTrip?.id}
                showDistance={currentTripData.showDistance}
              />
            </Group>
            <LayoutContainer>
              {basicTrip && <TextButton to={getTripURL(basicTrip.id)} kind="primary" size="large" fit="mobile-full-width">
                {intl.formatMessage({
                  id: 'ptl4J4czGo',
                  defaultMessage: 'Keep building your trip',
                  description: 'Button label to continue building trip in the trip planner feature',
                })}
              </TextButton>}
              {!basicTrip && <TextButton
                to={`/trip-planner/generate-trip?location=${currentTripData.location}`}
                kind="primary"
                size="large"
                fit="mobile-full-width"
                startIcon={<SolidSparklesIcon />}
              >{startPlanningLabel}
              </TextButton>}
            </LayoutContainer>
          </Group>
        </Sheet>
      </ContentWrapper>
    </BackgroundContainer>
  )
}

export default TripHomePage
